import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Ban, 
  Shield, 
  ShieldOff, 
  RefreshCw,
  Users,
  Calendar,
  Mail,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const UserModeration = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchUsers();
  }, [page, searchQuery]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', '20');
      if (searchQuery) params.append('search', searchQuery);

      const response = await axios.get(`/api/admin/users?${params}`);
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleBanToggle = async (userId, currentStatus) => {
    setActionLoading(prev => ({ ...prev, [userId]: 'ban' }));
    
    try {
      const response = await axios.put(`/api/admin/users/${userId}/ban`, {
        isBanned: !currentStatus
      });
      
      setUsers(prev => prev.map(user => 
        user._id === userId ? response.data.user : user
      ));
      
      toast.success(`User ${!currentStatus ? 'banned' : 'unbanned'} successfully`);
    } catch (error) {
      console.error('Failed to toggle ban status:', error);
      toast.error('Failed to update user status');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleRoleChange = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    setActionLoading(prev => ({ ...prev, [userId]: 'role' }));
    
    try {
      const response = await axios.put(`/api/admin/users/${userId}/role`, {
        role: newRole
      });
      
      setUsers(prev => prev.map(user => 
        user._id === userId ? response.data.user : user
      ));
      
      toast.success(`User role changed to ${newRole} successfully`);
    } catch (error) {
      console.error('Failed to change user role:', error);
      toast.error(error.response?.data?.message || 'Failed to change user role');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-serif font-bold text-text-primary">
          User Moderation
        </h2>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search users..."
              className="pl-10 pr-4 py-2 glass-morphism rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
            />
          </div>
          <button
            onClick={fetchUsers}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-20">
          <Users className="w-16 h-16 text-text-muted mx-auto mb-4" />
          <h3 className="text-xl font-serif font-semibold text-text-primary mb-2">
            No users found
          </h3>
          <p className="text-text-secondary">
            {searchQuery ? 'Try adjusting your search terms' : 'No users have registered yet'}
          </p>
        </div>
      ) : (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="glass-morphism border-b border-glass-border">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-glass-border">
                {users.map((user, index) => (
                  <motion.tr
                    key={user._id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="hover:bg-glass-bg/30 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-accent-primary/20 rounded-full flex items-center justify-center mr-3">
                          <span className="text-accent-primary font-medium">
                            {user.username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-text-primary">
                            {user.username}
                          </div>
                          <div className="text-sm text-text-secondary">
                            ID: {user._id.slice(-8)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-text-primary">
                        <Mail className="w-4 h-4 mr-2 text-text-muted" />
                        {user.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.role === 'admin' 
                          ? 'bg-accent-primary/20 text-accent-primary' 
                          : 'bg-text-muted/20 text-text-secondary'
                      }`}>
                        {user.role === 'admin' ? (
                          <Shield className="w-3 h-3 mr-1" />
                        ) : (
                          <Users className="w-3 h-3 mr-1" />
                        )}
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.isBanned 
                          ? 'bg-red-500/20 text-red-400' 
                          : 'bg-green-500/20 text-green-400'
                      }`}>
                        {user.isBanned ? (
                          <Ban className="w-3 h-3 mr-1" />
                        ) : (
                          <Shield className="w-3 h-3 mr-1" />
                        )}
                        {user.isBanned ? 'Banned' : 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-2 text-text-muted" />
                        {new Date(user.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleRoleChange(user._id, user.role)}
                          disabled={actionLoading[user._id] === 'role'}
                          className="p-2 glass-morphism rounded-lg hover:bg-glass-bg/50 transition-colors disabled:opacity-50"
                          title={user.role === 'admin' ? 'Remove admin' : 'Make admin'}
                        >
                          {actionLoading[user._id] === 'role' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : user.role === 'admin' ? (
                            <ShieldOff className="w-4 h-4 text-text-secondary" />
                          ) : (
                            <Shield className="w-4 h-4 text-accent-primary" />
                          )}
                        </button>
                        <button
                          onClick={() => handleBanToggle(user._id, user.isBanned)}
                          disabled={actionLoading[user._id] === 'ban'}
                          className={`p-2 glass-morphism rounded-lg hover:bg-glass-bg/50 transition-colors disabled:opacity-50 ${
                            user.isBanned ? 'text-green-400 hover:text-green-300' : 'text-red-400 hover:text-red-300'
                          }`}
                          title={user.isBanned ? 'Unban user' : 'Ban user'}
                        >
                          {actionLoading[user._id] === 'ban' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : user.isBanned ? (
                            <Shield className="w-4 h-4" />
                          ) : (
                            <Ban className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="px-6 py-4 border-t border-glass-border">
              <div className="flex items-center justify-between">
                <div className="text-sm text-text-secondary">
                  Showing {((page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} results
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPage(prev => Math.max(1, prev - 1))}
                    disabled={page === 1}
                    className="p-2 glass-morphism rounded-lg disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="text-text-secondary px-3">
                    Page {page} of {pagination.pages}
                  </span>
                  <button
                    onClick={() => setPage(prev => Math.min(pagination.pages, prev + 1))}
                    disabled={page === pagination.pages}
                    className="p-2 glass-morphism rounded-lg disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserModeration;
